import React from 'react';
import { Box, Typography } from '@mui/material';
import { resolveSlotValues, getDTWidgetSlots } from '../../registry/dtWidgetTypes';
import { useDTDemoMap } from '../../hooks/useDTDemoTick';
import { hasLiveSlotValue } from '../../utils/dtDemoFallback';
import DTRadialGauge from './DTRadialGauge';

const DEFAULT_SLOTS = getDTWidgetSlots('dt-breaker-data');

const FAULT_SLOT_KEYS = ['faultCounterL', 'faultCounterI', 'faultCounterG', 'faultCounterS'];
const OPERATION_SLOT_KEYS = ['onOperation', 'offOperation'];
const WEAR_SLOT_KEYS = ['contactWearR', 'contactWearY', 'contactWearB'];

const WEAR_KEY_MAP = { contactWearR: 'contactWearR', contactWearY: 'contactWearY', contactWearB: 'contactWearB' };

function DTBreakerData({ widget, data, theme }) {
  const isDark = theme === 'dark';
  const tagprops = widget?.props?.extended?.tagprops || [];
  const liveData = Array.isArray(data) ? data : [];
  const slotValues = resolveSlotValues(tagprops, liveData);
  const extrasDemo = useDTDemoMap('dt-breaker-data-extras');

  const wearTagprops = tagprops
    .filter((tp) => WEAR_SLOT_KEYS.includes(tp.slotKey))
    .map((tp) => ({ ...tp, slotKey: WEAR_KEY_MAP[tp.slotKey] || tp.slotKey }));
  const radialWidget = { ...widget, props: { ...widget?.props, extended: { tagprops: wearTagprops } } };

  const faultSlots = DEFAULT_SLOTS.filter((s) => FAULT_SLOT_KEYS.includes(s.key));
  const operationSlots = DEFAULT_SLOTS.filter((s) => OPERATION_SLOT_KEYS.includes(s.key));

  return (
    <Box sx={{ flex: 1, minHeight: 0, width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', p: 1, boxSizing: 'border-box' }}>
      <Box sx={{ flex: '1 1 52%', minHeight: 100, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <DTRadialGauge widget={radialWidget} data={data} theme={theme} />
      </Box>

      <Box sx={{ flex: '1 1 48%', minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', mt: 0.5 }}>
        <Typography variant="subtitle2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 0.25, borderBottom: '1px solid #B0B0B0', flexShrink: 0 }}>
          Fault Counter
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
          {faultSlots.map((slot) => {
            const resolved = slotValues[slot.key];
            const liveOk = hasLiveSlotValue(resolved);
            const displayVal = liveOk ? resolved.value : extrasDemo[slot.key] ?? '—';
            return (
              <Box key={slot.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '1 1 0', minHeight: 0 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#555' }}>
                  {resolved?.alias || slot.label}
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 600 }}>
                  {displayVal}
                </Typography>
              </Box>
            );
          })}
        </Box>

        <Typography variant="subtitle2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 700, mb: 0.25, mt: 0.5, borderBottom: '1px solid #B0B0B0', flexShrink: 0 }}>
          Operations
        </Typography>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly' }}>
          {operationSlots.map((slot) => {
            const resolved = slotValues[slot.key];
            const liveOk = hasLiveSlotValue(resolved);
            const displayVal = liveOk ? resolved.value : extrasDemo[slot.key] ?? '—';
            return (
              <Box key={slot.key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flex: '1 1 0', minHeight: 0 }}>
                <Typography variant="body2" sx={{ color: isDark ? '#aaa' : '#555' }}>
                  {resolved?.alias || slot.label}
                </Typography>
                <Typography variant="body2" sx={{ color: isDark ? '#fff' : '#000', fontWeight: 600 }}>
                  {displayVal}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
}

export default React.memo(DTBreakerData);
